/**
 * useReminderSettings — per-parameter test reminder preferences (bonus C).
 *
 * Persisted on the Pi (namespace `test-kit-reminder-settings`) via
 * `useServerBackedState`. The legacy localStorage copy
 * (`reefapp.testKits.reminderSettings`) seeds the default and is never
 * deleted. Default: all parameters disabled, thresholdDays = 7.
 */
import { useState, useCallback, useRef } from 'react';
import type { ParameterKey } from '@reefapp/shared';
import { useServerBackedState } from '../hooks/useServerBackedState';

export interface ReminderSetting {
  enabled: boolean;
  thresholdDays: number;
}

export type ReminderSettings = Readonly<Record<string, ReminderSetting>>;

/** Legacy browser-only key — read as the seed default, never deleted. */
const LEGACY_STORAGE_KEY = 'reefapp.testKits.reminderSettings';

/** `/api/userdata` namespace holding the reminder settings on the Pi. */
export const REMINDER_SETTINGS_NAMESPACE = 'test-kit-reminder-settings';

function readLegacy(): ReminderSettings {
  try {
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as ReminderSettings;
      }
    }
  } catch {
    // corrupt — use defaults
  }
  return {};
}

export interface UseReminderSettingsReturn {
  settings: ReminderSettings;
  setEnabled(parameterKey: ParameterKey, enabled: boolean): void;
  setThresholdDays(parameterKey: ParameterKey, days: number): void;
}

export function useReminderSettings(): UseReminderSettingsReturn {
  const [legacyDefault] = useState(readLegacy);
  const { value, setValue } = useServerBackedState<ReminderSettings>(
    REMINDER_SETTINGS_NAMESPACE,
    legacyDefault,
  );
  // Tolerate a malformed server blob.
  const settings: ReminderSettings =
    value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const setEnabled = useCallback(
    (parameterKey: ParameterKey, enabled: boolean) => {
      const prev = settingsRef.current;
      setValue({
        ...prev,
        [parameterKey]: {
          ...prev[parameterKey],
          enabled,
          thresholdDays: prev[parameterKey]?.thresholdDays ?? 7,
        },
      });
    },
    [setValue],
  );

  const setThresholdDays = useCallback(
    (parameterKey: ParameterKey, days: number) => {
      const prev = settingsRef.current;
      setValue({
        ...prev,
        [parameterKey]: {
          ...prev[parameterKey],
          enabled: prev[parameterKey]?.enabled ?? false,
          thresholdDays: days,
        },
      });
    },
    [setValue],
  );

  return { settings, setEnabled, setThresholdDays };
}
